import React, { useCallback, useEffect, useState } from 'react';
import { McButton, McIcon, McInput, McSelect, McOption, McCheckbox, McTextarea, McTooltip } from '@maersk-global/mds-react-wrapper';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ColumnDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'date' | 'select' | 'ref';
  required?: boolean;
  immutable?: boolean;
  /** For type=select */
  options?: string[];
  /** For type=ref */
  refTable?: string;
  refValueKey?: string;
  refDisplayKey?: string;
}

interface ColumnBuilderProps {
  /** Serialised JSON string (the value stored in columnDefs) */
  value: string;
  onChange: (json: string) => void;
  disabled?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseValue(json: string): ColumnDef[] {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function newCol(): ColumnDef {
  return { key: '', label: '', type: 'text', required: false, immutable: false };
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = {
  grid: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const,
    fontSize: '0.82rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.4rem 0.5rem',
    backgroundColor: 'var(--mds-color-neutral-08, #f5f5f5)',
    borderBottom: '2px solid var(--mds-color-neutral-20)',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    fontSize: '0.78rem',
    color: 'var(--mds-color-neutral-60)',
  },
  td: {
    padding: '0.3rem 0.4rem',
    borderBottom: '1px solid var(--mds-color-neutral-10)',
    verticalAlign: 'middle' as const,
  },
  centerCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '40px',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.3rem 0.4rem',
    border: '1px solid var(--mds-color-neutral-30)',
    borderRadius: '4px',
    fontSize: '0.82rem',
    backgroundColor: 'white',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.3rem 0.4rem',
    border: '1px solid var(--mds-color-neutral-30)',
    borderRadius: '4px',
    fontSize: '0.82rem',
    backgroundColor: 'white',
  } as React.CSSProperties,
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: 'var(--mds-color-brand-primary)',
  } as React.CSSProperties,
  refSection: {
    display: 'flex',
    gap: '0.3rem',
    alignItems: 'center',
    marginTop: '0.25rem',
  } as React.CSSProperties,
  refInput: {
    flex: 1,
    padding: '0.25rem 0.35rem',
    border: '1px solid var(--mds-color-neutral-20)',
    borderRadius: '3px',
    fontSize: '0.78rem',
    backgroundColor: '#fafafa',
    fontFamily: 'monospace',
  } as React.CSSProperties,
  addBtn: {
    marginTop: '0.6rem',
    display: 'flex',
    justifyContent: 'flex-start',
  } as React.CSSProperties,
  moveBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0 2px',
    color: 'var(--mds-color-neutral-50)',
    fontSize: '0.8rem',
    lineHeight: 1,
  } as React.CSSProperties,
  deleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    color: 'var(--mds-color-feedback-error-text, #b00020)',
    borderRadius: '3px',
  } as React.CSSProperties,
  jsonError: {
    marginTop: '0.35rem',
    padding: '0.35rem 0.5rem',
    backgroundColor: '#fff3f3',
    border: '1px solid #f5c0c0',
    borderRadius: '4px',
    fontSize: '0.78rem',
    color: 'var(--mds-color-feedback-error-text, #b00020)',
  } as React.CSSProperties,
  rawToggle: {
    fontSize: '0.75rem',
    color: 'var(--mds-color-neutral-50)',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    marginLeft: '0.5rem',
  } as React.CSSProperties,
  rawTextarea: {
    width: '100%',
    fontFamily: 'monospace',
    fontSize: '0.78rem',
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid var(--mds-color-neutral-30)',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
};

const COLUMN_TYPES = ['text', 'number', 'boolean', 'date', 'select', 'ref'] as const;

// ─── Component ────────────────────────────────────────────────────────────────

export default function ColumnBuilder({ value, onChange, disabled = false }: ColumnBuilderProps) {
  const [cols, setCols] = useState<ColumnDef[]>(() => parseValue(value));
  const [showRaw, setShowRaw] = useState(false);
  const [rawText, setRawText] = useState(value);
  const [rawError, setRawError] = useState('');

  // Sync inbound value changes (e.g. when parent resets the form)
  useEffect(() => {
    setCols(parseValue(value));
    setRawText(value);
    setRawError('');
  }, [value]);

  // Emit serialised JSON whenever cols change
  const emit = useCallback(
    (next: ColumnDef[]) => {
      const json = JSON.stringify(
        next.map(c => {
          const out: Record<string, any> = { key: c.key, label: c.label, type: c.type };
          if (c.required) out.required = true;
          if (c.immutable) out.immutable = true;
          if (c.type === 'select' && c.options?.length) out.options = c.options;
          if (c.type === 'ref') {
            if (c.refTable) out.refTable = c.refTable;
            if (c.refValueKey) out.refValueKey = c.refValueKey;
            if (c.refDisplayKey) out.refDisplayKey = c.refDisplayKey;
          }
          return out;
        }),
        null,
        2,
      );
      setRawText(json);
      onChange(json);
    },
    [onChange],
  );

  function update(index: number, patch: Partial<ColumnDef>) {
    setCols(prev => {
      const next = prev.map((c, i) => (i === index ? { ...c, ...patch } : c));
      emit(next);
      return next;
    });
  }

  function addCol() {
    setCols(prev => {
      const next = [...prev, newCol()];
      emit(next);
      return next;
    });
  }

  function removeCol(index: number) {
    setCols(prev => {
      const next = prev.filter((_, i) => i !== index);
      emit(next);
      return next;
    });
  }

  function moveCol(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= cols.length) return;
    setCols(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      emit(next);
      return next;
    });
  }

  function handleRawChange(text: string) {
    setRawText(text);
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('Root must be a JSON array');
      setRawError('');
      setCols(parsed);
      onChange(text);
    } catch (e: any) {
      setRawError(e.message);
    }
  }

  if (showRaw) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.35rem' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--mds-color-neutral-50)' }}>
            Raw JSON editor
          </span>
          <McButton variant="plain" fit="small" appearance="neutral" onClick={() => setShowRaw(false)}>
            <McIcon icon="arrow-left" slot="prefix" />
            Visual editor
          </McButton>
        </div>
        <McTextarea
          hiddenlabel
          label="Column definitions JSON"
          value={rawText}
          rows={12}
          disabled={disabled}
          input={(e: any) => handleRawChange(e.detail?.value ?? e.target?.value ?? '')}
        />
        {rawError && <div style={s.jsonError}>⚠ {rawError}</div>}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--mds-color-neutral-50)' }}>
          {cols.length} column{cols.length !== 1 ? 's' : ''} defined
        </span>
        <McButton variant="plain" fit="small" appearance="neutral" onClick={() => setShowRaw(true)}>
          <McIcon icon="code" slot="prefix" />
          Raw JSON
        </McButton>
      </div>

      {cols.length === 0 && !disabled && (
        <p style={{ fontSize: '0.8rem', color: 'var(--mds-color-neutral-50)', marginBottom: '0.5rem' }}>
          No columns yet — click <strong>Add Column</strong> to start.
        </p>
      )}

      {cols.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.grid}>
            <colgroup>
              <col style={{ width: '36px' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '64px' }} />
              <col style={{ width: '88px' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '48px' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={s.th}></th>
                <th style={s.th}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Key *</span>
                    
                  </div>
                </th>
                <th style={s.th}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Label</span>
                    
                  </div>
                </th>
                <th style={s.th}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Type</span>
                    
                  </div>
                </th>
                <th style={{ ...s.th, textAlign: 'center' as const }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Req</span>
                    
                  </div>
                </th>
                <th style={{ ...s.th, textAlign: 'center' as const }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Immut</span>
                    
                  </div>
                </th>
                <th style={s.th}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span>Options / Ref config</span>
                    
                  </div>
                </th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {cols.map((col, i) => (
                <tr key={i}>
                  {/* Reorder */}
                  <td style={{ ...s.td, padding: '0 2px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' as const }}>
                      <McButton variant="plain" fit="small" appearance="neutral" disabled={disabled || i === 0} onClick={() => moveCol(i, -1)} title="Move up">
                        <McIcon icon="chevron-up" />
                      </McButton>
                      <McButton variant="plain" fit="small" appearance="neutral" disabled={disabled || i === cols.length - 1} onClick={() => moveCol(i, 1)} title="Move down">
                        <McIcon icon="chevron-down" />
                      </McButton>
                    </div>
                  </td>

                  {/* Key */}
                  <td style={{ ...s.td, minWidth: '110px' }}>
                    <McInput
                      hiddenlabel
                      label="Key"
                      fit="small"
                      style={{ width: '100%' }}
                      value={col.key}
                      placeholder="snake_key"
                      disabled={disabled}
                      input={(e: any) => update(i, { key: (e.detail?.value ?? e.target?.value ?? '').replace(/\s/g, '_') })}
                    />
                  </td>

                  {/* Label */}
                  <td style={{ ...s.td, minWidth: '110px' }}>
                    <McInput
                      hiddenlabel
                      label="Display name"
                      fit="small"
                      style={{ width: '100%' }}
                      value={col.label}
                      placeholder="Display Name"
                      disabled={disabled}
                      input={(e: any) => update(i, { label: e.detail?.value ?? e.target?.value ?? '' })}
                    />
                  </td>

                  {/* Type */}
                  <td style={{ ...s.td, minWidth: '90px' }}>
                    <McSelect
                      hiddenlabel
                      label="Type"
                      fit="small"
                      style={{ width: '100%' }}
                      value={col.type}
                      disabled={disabled}
                      optionselected={(e: any) =>
                        update(i, {
                          type: (e.detail?.value ?? 'text') as ColumnDef['type'],
                          options: undefined,
                          refTable: undefined,
                          refValueKey: undefined,
                          refDisplayKey: undefined,
                        })
                      }
                    >
                      {COLUMN_TYPES.map(t => (
                        <McOption key={t} value={t}>
                          {t}
                        </McOption>
                      ))}
                    </McSelect>
                  </td>

                  {/* Required */}
                  <td style={{ ...s.td, textAlign: 'center' as const }}>
                    <div style={s.centerCell}>
                      <McCheckbox
                        hiddenlabel
                        label="Required"
                        checked={!!col.required}
                        disabled={disabled}
                        change={(e: any) => update(i, { required: e.detail?.checked ?? e.target?.checked ?? false })}
                      />
                    </div>
                  </td>

                  {/* Immutable */}
                  <td style={{ ...s.td, textAlign: 'center' as const }}>
                    <div style={s.centerCell}>
                      <McCheckbox
                        hiddenlabel
                        label="Immutable"
                        checked={!!col.immutable}
                        disabled={disabled}
                        title="Prevent edits after row is created"
                        change={(e: any) => update(i, { immutable: e.detail?.checked ?? e.target?.checked ?? false })}
                      />
                    </div>
                  </td>

                  {/* Options / Ref config */}
                  <td style={{ ...s.td, minWidth: '200px' }}>
                    {col.type === 'select' && (
                      <McInput
                        hiddenlabel
                        label="Options"
                        fit="small"
                        style={{ width: '100%' }}
                        value={(col.options ?? []).join(', ')}
                        placeholder="opt1, opt2, opt3"
                        disabled={disabled}
                        title="Comma-separated option values"
                        input={(e: any) =>
                          update(i, {
                            options: (e.detail?.value ?? e.target?.value ?? '')
                              .split(',')
                              .map((s: string) => s.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    )}
                    {col.type === 'ref' && (
                      <div>
                        <div style={s.refSection}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-50)', minWidth: '72px' }}>refTable</span>
                          <McInput
                            hiddenlabel
                            label="refTable"
                            fit="small"
                            style={{ width: '100%' }}
                            value={col.refTable ?? ''}
                            placeholder="che_type"
                            disabled={disabled}
                            input={(e: any) => update(i, { refTable: e.detail?.value ?? e.target?.value ?? '' })}
                          />
                        </div>
                        <div style={s.refSection}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-50)', minWidth: '72px' }}>valueKey</span>
                          <McInput
                            hiddenlabel
                            label="valueKey"
                            fit="small"
                            style={{ width: '100%' }}
                            value={col.refValueKey ?? ''}
                            placeholder="code"
                            disabled={disabled}
                            input={(e: any) => update(i, { refValueKey: e.detail?.value ?? e.target?.value ?? '' })}
                          />
                        </div>
                        <div style={s.refSection}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--mds-color-neutral-50)', minWidth: '72px' }}>displayKey</span>
                          <McInput
                            hiddenlabel
                            label="displayKey"
                            fit="small"
                            style={{ width: '100%' }}
                            value={col.refDisplayKey ?? ''}
                            placeholder="name"
                            disabled={disabled}
                            input={(e: any) => update(i, { refDisplayKey: e.detail?.value ?? e.target?.value ?? '' })}
                          />
                        </div>
                      </div>
                    )}
                    {col.type !== 'select' && col.type !== 'ref' && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--mds-color-neutral-30)' }}>—</span>
                    )}
                  </td>

                  {/* Delete */}
                  <td style={{ ...s.td, textAlign: 'center' as const }}>
                    {!disabled && (
                      <McButton variant="plain" fit="small" appearance="error" title="Remove column" onClick={() => removeCol(i)}>
                        <McIcon icon="trash" />
                      </McButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!disabled && (
        <div style={s.addBtn}>
          <McButton variant="outlined" fit="small" onClick={addCol}>
            <McIcon icon="plus" slot="prefix" />
            Add Column
          </McButton>
        </div>
      )}
    </div>
  );
}
